import type { Camera } from './camera.ts';

export interface InputHandlers {
  /** A click or tap that did not turn into a drag, in element px; `pointer` is mouse, touch or pen. */
  readonly onTap: (
    sx: number,
    sy: number,
    mods: { readonly shiftKey: boolean; readonly pointer: string },
  ) => void;
  /**
   * A long press that did not move (touch, pen), or a right click: "show me", never an action
   * (docs/ux.md U-TOUCH-03). The tap that would follow is dropped.
   */
  readonly onHold?: (sx: number, sy: number) => void;
  /** Pointer over the board without buttons pressed, or null when it leaves. */
  readonly onHover: (sx: number, sy: number) => void;
  readonly onLeave: () => void;
  /** Whether a drag is in progress (for the cursor). */
  readonly onDragChange: (dragging: boolean) => void;
}

/** Movement that turns a press into a drag, CSS px. */
const DRAG_THRESHOLD = { mouse: 4, touch: 8 } as const;
/** How long a still press must last to count as a long press, ms (U-TOUCH-03). */
export const HOLD_MS = 450;
/** A pause longer than this starts a new wheel gesture. */
const WHEEL_GESTURE_GAP_MS = 220;
const KEY_PAN_PX = 96;
/** The fling's speed is measured over the drag's last this many ms. */
const FLING_WINDOW_MS = 100;
/** A release slower than this (CSS px per ms) just stops; faster, the map glides on. */
const FLING_MIN_SPEED = 0.15;
/** A finger held still this long before lifting means "stop here". */
const FLING_STALE_MS = 80;
/** The fastest glide, CSS px per ms: a wild flick does not throw the map across the sea. */
const FLING_MAX_SPEED = 5;
const KEY_ZOOM = 1.25;

export type WheelIntent = 'zoom' | 'pan';

/**
 * Pinch (ctrl+wheel in Chromium and Firefox) and a mouse wheel zoom; two-finger trackpad
 * scrolling pans. A mouse wheel moves in whole lines or in big integer steps (Chrome on macOS:
 * multiples of 4.000244), a trackpad in small or fractional ones, and sideways too.
 */
export function wheelIntent(e: Pick<WheelEvent, 'ctrlKey' | 'deltaMode' | 'deltaX' | 'deltaY'>): WheelIntent {
  if (e.ctrlKey) return 'zoom';
  if (e.deltaMode !== 0) return 'zoom';
  if (e.deltaX !== 0) return 'pan';
  const dy = Math.abs(e.deltaY);
  if (dy !== 0 && dy % 4.000244140625 === 0) return 'zoom';
  return Number.isInteger(dy) && dy >= 50 ? 'zoom' : 'pan';
}

/**
 * Zoom factor for one wheel event. Pinches report small deltas many times a second; a mouse
 * notch is about 100 px and zooms by ~1.3×. One event never zooms by more than ~2.3×.
 */
export function wheelZoomFactor(e: Pick<WheelEvent, 'ctrlKey' | 'deltaMode' | 'deltaY'>): number {
  const dy = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
  if (e.ctrlKey) return Math.exp(-Math.max(-60, Math.min(60, dy)) * 0.012);
  return 2 ** (-Math.max(-300, Math.min(300, dy)) / 250);
}

interface Press {
  readonly id: number;
  readonly type: string;
  x: number;
  y: number;
  readonly startX: number;
  readonly startY: number;
  /** A long press fired: the release is not a tap. */
  held: boolean;
}

/** Wires wheel, pinch, drag, tap and keyboard on `el` to the camera. Returns a detach function. */
export function attachInput(el: HTMLElement, camera: Camera, h: InputHandlers): () => void {
  const local = (e: { clientX: number; clientY: number }) => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  // -- wheel -----------------------------------------------------------------
  let gesture: { intent: WheelIntent; at: number } | null = null;
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const now = performance.now();
    // Classify a gesture once, from its first event, so a fling never flips into a zoom.
    if (!gesture || now - gesture.at > WHEEL_GESTURE_GAP_MS || (e.ctrlKey && gesture.intent === 'pan')) {
      gesture = { intent: wheelIntent(e), at: now };
    }
    gesture.at = now;
    const p = local(e);
    if (gesture.intent === 'zoom') camera.zoomAt(p.x, p.y, wheelZoomFactor(e));
    else camera.panBy(e.shiftKey && e.deltaX === 0 ? -e.deltaY : -e.deltaX, e.shiftKey ? 0 : -e.deltaY);
  };

  // -- Safari trackpad pinch (GestureEvent, no ctrl+wheel) --------------------
  let gestureScale = 1;
  const onGestureStart = (e: Event) => {
    e.preventDefault();
    gestureScale = 1;
  };
  const onGestureChange = (e: Event) => {
    e.preventDefault();
    const g = e as Event & { scale: number; clientX: number; clientY: number };
    const p = local(g);
    camera.zoomAt(p.x, p.y, g.scale / gestureScale);
    gestureScale = g.scale;
  };

  // -- pointers: drag to pan, tap, two-finger pinch --------------------------
  const presses = new Map<number, Press>();
  let dragging = false;
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  const cancelHold = () => {
    if (holdTimer !== null) clearTimeout(holdTimer);
    holdTimer = null;
  };
  let pinch: { dist: number; mx: number; my: number } | null = null;
  /** The drag's recent positions, for the speed of a fling. */
  let track: { x: number; y: number; t: number }[] = [];
  const record = (x: number, y: number, t: number) => {
    track.push({ x, y, t });
    while (track.length > 2 && t - (track[0]?.t ?? t) > FLING_WINDOW_MS) track.shift();
  };
  const fling = (now: number) => {
    const a = track[0];
    const b = track[track.length - 1];
    track = [];
    if (!a || !b || b.t - a.t < 8 || now - b.t > FLING_STALE_MS) return;
    const vx = (b.x - a.x) / (b.t - a.t);
    const vy = (b.y - a.y) / (b.t - a.t);
    const speed = Math.hypot(vx, vy);
    const k = Math.min(1, FLING_MAX_SPEED / speed);
    if (speed >= FLING_MIN_SPEED) camera.glide(vx * k, vy * k);
  };

  const setDragging = (v: boolean) => {
    if (v === dragging) return;
    dragging = v;
    h.onDragChange(v);
  };
  const pinchOf = () => {
    const [a, b] = [...presses.values()];
    if (!a || !b) return null;
    return { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 1) return;
    if (e.button === 1) e.preventDefault(); // no autoscroll
    camera.stopFlight();
    // The first finger down: no other touch is on the glass, whatever was missed. iOS Safari
    // may drop a touch's up and cancel when the page changes under it; left in, that touch
    // would turn the next one-finger drag into a pinch.
    if (e.pointerType !== 'mouse' && e.isPrimary) {
      for (const [id, press] of presses) if (press.type !== 'mouse') presses.delete(id);
      if (presses.size === 0) pinch = null;
    }
    el.setPointerCapture(e.pointerId);
    const p = local(e);
    track = [];
    presses.set(e.pointerId, {
      id: e.pointerId,
      type: e.pointerType,
      x: p.x,
      y: p.y,
      startX: p.x,
      startY: p.y,
      held: false,
    });
    cancelHold();
    if (e.pointerType !== 'mouse' && presses.size === 1 && h.onHold) {
      const id = e.pointerId;
      holdTimer = setTimeout(() => {
        holdTimer = null;
        const press = presses.get(id);
        if (!press || dragging || presses.size !== 1) return;
        press.held = true;
        if ('vibrate' in navigator) navigator.vibrate(10);
        h.onHold?.(press.x, press.y);
      }, HOLD_MS);
    }
    // The middle button always pans; the left one only once it moves.
    if (e.button === 1) setDragging(true);
    if (presses.size === 2) {
      cancelHold();
      pinch = pinchOf();
      setDragging(true);
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    const p = local(e);
    const press = presses.get(e.pointerId);
    if (!press) {
      if (presses.size === 0) h.onHover(p.x, p.y);
      return;
    }
    const dx = p.x - press.x;
    const dy = p.y - press.y;
    press.x = p.x;
    press.y = p.y;
    if (presses.size >= 2) {
      track = [];
      const next = pinchOf();
      if (pinch && next) {
        camera.zoomAt(next.mx, next.my, next.dist / pinch.dist);
        camera.panBy(next.mx - pinch.mx, next.my - pinch.my);
      }
      pinch = next;
      return;
    }
    if (!dragging) {
      const limit = press.type === 'mouse' ? DRAG_THRESHOLD.mouse : DRAG_THRESHOLD.touch;
      if (Math.hypot(p.x - press.startX, p.y - press.startY) < limit) return;
      cancelHold();
      setDragging(true);
      camera.panBy(p.x - press.startX, p.y - press.startY);
      record(p.x, p.y, e.timeStamp);
      return;
    }
    camera.panBy(dx, dy);
    record(p.x, p.y, e.timeStamp);
  };

  const onPointerUp = (e: PointerEvent) => {
    const press = presses.get(e.pointerId);
    if (!press) return;
    presses.delete(e.pointerId);
    cancelHold();
    if (presses.size === 1) {
      // Lifting one finger of a pinch: keep panning with the other, never tap.
      pinch = null;
      return;
    }
    if (presses.size > 0) return;
    const wasDragging = dragging;
    setDragging(false);
    pinch = null;
    if (wasDragging && e.type === 'pointerup') fling(e.timeStamp);
    else track = [];
    if (!wasDragging && !press.held && e.type === 'pointerup') {
      h.onTap(press.x, press.y, { shiftKey: e.shiftKey, pointer: press.type });
    }
  };

  // The browser let go of a pointer without an up or a cancel: forget it, with no tap. WebKit
  // may tell of the lost capture just before the up, so the up (and its fling) goes first.
  const onLostCapture = (e: PointerEvent) => {
    const { pointerId, pointerType } = e;
    setTimeout(() => {
      if (presses.has(pointerId)) onPointerUp(new PointerEvent('pointercancel', { pointerId, pointerType }));
    }, 0);
  };

  const onPointerLeave = () => {
    if (presses.size === 0) h.onLeave();
  };

  // -- keyboard (when the board has focus) -----------------------------------
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const pan: Record<string, [number, number]> = {
      ArrowLeft: [KEY_PAN_PX, 0],
      ArrowRight: [-KEY_PAN_PX, 0],
      ArrowUp: [0, KEY_PAN_PX],
      ArrowDown: [0, -KEY_PAN_PX],
      a: [KEY_PAN_PX, 0],
      d: [-KEY_PAN_PX, 0],
      w: [0, KEY_PAN_PX],
      s: [0, -KEY_PAN_PX],
    };
    const step = pan[e.key];
    if (step) {
      const v = camera.getSnapshot();
      camera.flyTo({ ...v, x: v.x - step[0] / v.zoom, y: v.y - step[1] / v.zoom }, 120);
    } else if (e.key === '+' || e.key === '=') camera.zoomBy(KEY_ZOOM);
    else if (e.key === '-' || e.key === '_') camera.zoomBy(1 / KEY_ZOOM);
    else if (e.key === '0') camera.fit(true);
    else return;
    e.preventDefault();
  };

  const onContextMenu = (e: Event) => {
    e.preventDefault();
    if (dragging || !h.onHold) return;
    // A long touch may bring the browser's own context menu first: count it as the hold.
    const press = [...presses.values()][0];
    if (press) {
      if (press.held) return;
      press.held = true;
      cancelHold();
      h.onHold(press.x, press.y);
      return;
    }
    // A right click shows the unit's card (U-CTX-01).
    const p = local(e as MouseEvent);
    h.onHold(p.x, p.y);
  };

  const opts = { passive: false } as const;
  el.addEventListener('wheel', onWheel, opts);
  el.addEventListener('gesturestart', onGestureStart, opts);
  el.addEventListener('gesturechange', onGestureChange, opts);
  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerUp);
  el.addEventListener('lostpointercapture', onLostCapture);
  el.addEventListener('pointerleave', onPointerLeave);
  el.addEventListener('keydown', onKeyDown);
  el.addEventListener('contextmenu', onContextMenu);
  return () => {
    cancelHold();
    el.removeEventListener('wheel', onWheel);
    el.removeEventListener('gesturestart', onGestureStart);
    el.removeEventListener('gesturechange', onGestureChange);
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointermove', onPointerMove);
    el.removeEventListener('pointerup', onPointerUp);
    el.removeEventListener('pointercancel', onPointerUp);
    el.removeEventListener('lostpointercapture', onLostCapture);
    el.removeEventListener('pointerleave', onPointerLeave);
    el.removeEventListener('keydown', onKeyDown);
    el.removeEventListener('contextmenu', onContextMenu);
  };
}
